import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useMediaQuery, useTheme } from '@material-ui/core';
import {
  FiEye as PreviewIcon,
  FiDownload as DownloadIcon,
  FiSend as SendIcon,
  FiXCircle as CancelIcon,
} from 'react-icons/fi';
import { compact } from 'lodash';
import useNavigate from 'src/hooks/useNavigate';
import { deleteSignatureStatusForFileId } from 'src/store/files/actions';
import { FilesActionsRendererProps } from 'src/components/AgGrid/Renderer/types';
import { BaseActionsMenuButton } from 'src/components/Dropdowns';
import {
  FILE_TYPE,
  SIGNATURE_PAGE,
  FILES_ACTION,
  UPGRADE_TOOLTIP_LABEL,
  FOLDER_FILE_KEY,
  FILE_ACTION_DISABLE_TOOLTIP_LABEL,
} from 'src/constants';
import { TrashIcon, EditIcon } from 'src/components/Icons';
import { FileUtils } from 'src/utils';
import { RootState } from 'src/store/reduxTypes';
import { FileResponse, FilesTableActionInput } from 'src/store/files/types';
import {
  getExtensionFromFileKey,
  getFileNameFromFileKey,
} from 'src/components/Files/helpers';
import { RouteContext } from 'src/context';

interface QueryParamProps {
  clientUserId?: string;
}

const getFilesInFolder = (fileData: any, files: Array<FileResponse>) =>
  FileUtils.getAllFilesFromPath({
    files,
    sourcePath: fileData.filePath.join('/'),
    fileType: fileData.type,
    fileKey: '',
    ignoreLinks: true,
  }).filter(f => f.fields.fileKey !== FOLDER_FILE_KEY);

/**
 * This function handle saving new custom field value
 * @param data row data for this table row, used to determine if this is a file or a folder
 * @param files from the redux state, a list of all files
 * @param filesTableAction function to trigger a download
 */
const getDownloadFunction = (
  fileData: any,
  files: Array<FileResponse>,
  filesTableAction: (data: FilesTableActionInput) => void | null,
) => {
  if (fileData.type === FILE_TYPE.FOLDER) {
    // using the path of the folder we want to find all FILE_METADATA objects nested inside this folder
    const formattedFileDataInFolder = getFilesInFolder(fileData, files)
      // map them into objects containing key, path, and identityID which the API will use to create a zip file
      .map(f => ({
        key: f.fields.fileKey,
        name: `${f.fields.fileName ||
          getFileNameFromFileKey(f.fields.fileKey)}${getExtensionFromFileKey(
          f.fields.fileKey,
        )}`,
        path: f.fields.path
          ? f.fields.path
              .split('/')
              .slice(fileData.filePath.length) // slice path of the file based on path depth of the targeted folder for download
              .join('/') // if path is present on the file object, create an altered path param omitting the first (parent) directory which API will already have
          : '',
        identityID: f.identityId,
      }));
    // return a func that will call the files table action with parameters that will trigger the .zip file download flow
    return formattedFileDataInFolder.length > 0
      ? () =>
          filesTableAction({
            actionType: FILES_ACTION.DOWNLOAD,
            fileData,
            fileBundleData: formattedFileDataInFolder,
            bundleName: fileData.fileName,
          })
      : null;
  }
  return !fileData.fileUrl
    ? () => filesTableAction({ actionType: FILES_ACTION.DOWNLOAD, fileData })
    : null;
};
export const FilesActionsRenderer: React.FC<FilesActionsRendererProps> = ({
  isClient,
  eSignEnabledInPlan,
  fileSettings,
  data,
  removeFile,
  renameFile,
  filesTableAction,
  onActionMenuClick,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isRequestingSignatureDisabled = () => {
    if (isSmallScreen) return true;
    return !eSignEnabledInPlan;
  };

  const { pathname, query } = React.useContext(RouteContext);
  const { clientUserId } = query as QueryParamProps;
  const { navigate } = useNavigate();
  const dispatch = useDispatch();
  const allFiles = useSelector((state: RootState) => state.files.items);
  const fileType = data.type;

  const downloadFunction = getDownloadFunction(
    data,
    allFiles,
    filesTableAction,
  );

  const downloadFile = React.useCallback(downloadFunction || (() => null), [
    allFiles,
  ]);
  const isEmptyFolder =
    fileType === FILE_TYPE.FOLDER &&
    getFilesInFolder(data, allFiles).length === 0;

  const previewFileActionLabel = data.fileUrl
    ? FILES_ACTION.VIEW
    : FILES_ACTION.PREVIEW;

  const signatureStatusField = data.signatureStatus;
  const isSignedFile =
    signatureStatusField &&
    (signatureStatusField === 'done' || signatureStatusField === 'signed') &&
    data.lastEsigModifierIdentityID;

  const previewFile =
    fileType !== FILE_TYPE.FOLDER
      ? () =>
          filesTableAction({
            actionType: previewFileActionLabel,
            fileData: data,
            fileLinkURLs: data.fileUrl ? [data.fileUrl] : undefined,
            isSignedFile,
          })
      : null;

  const eSignDocument =
    isClient &&
    fileType !== FILE_TYPE.FOLDER &&
    (signatureStatusField === 'pending' || signatureStatusField === 'sent') &&
    signatureStatusField !== 'done'
      ? () => {
          const { fileId, channelId, selectedFilePath } = data;
          const signaturePageLink = `${
            SIGNATURE_PAGE.path
          }?fileId=${fileId}&channelId=${channelId}&selectedFilePath=${selectedFilePath ||
            ''}`;
          navigate(signaturePageLink);
        }
      : null;

  // Show eSign request for the internal user in the files table actions
  const fileExtension = data.fileKey.split('.').pop();
  const eSignatureRequest =
    !isClient &&
    fileExtension === 'pdf' &&
    signatureStatusField !== 'pending' &&
    signatureStatusField !== 'sent' &&
    signatureStatusField !== 'done'
      ? () => {
          const { fileId, channelId, selectedFilePath } = data;
          const signaturePageLink = `${
            SIGNATURE_PAGE.path
          }?fileId=${fileId}&channelId=${channelId}&selectedFilePath=${selectedFilePath ||
            ''}&from=${pathname}&clientUserId=${clientUserId || ''} `; // clientUserId will be used to navigate back to client details page, 'from' will be used to navigate back to the last page
          navigate(signaturePageLink);
        }
      : null;

  const cancelESignatureRequest =
    !isClient &&
    fileType !== FILE_TYPE.FOLDER &&
    signatureStatusField &&
    (signatureStatusField === 'pending' || signatureStatusField === 'sent') &&
    signatureStatusField !== 'done'
      ? () => dispatch(deleteSignatureStatusForFileId(data.fileId))
      : null;

  // Show download signed file in the files table actions
  const downloadSignedFile = isSignedFile
    ? () =>
        filesTableAction({
          actionType: FILES_ACTION.DOWNLOAD,
          fileData: data,
          isSignedFile,
        })
    : null;

  const isDownloadAvailable = downloadFunction !== null;

  const fileOrLinkSuffix = isDownloadAvailable ? 'file' : 'link';
  const itemLabel = data.type === FILE_TYPE.FILE ? fileOrLinkSuffix : 'folder';
  const viewLabel = isDownloadAvailable ? `Preview ${itemLabel}` : 'Open link';
  const previewFileAction = previewFile
    ? [
        {
          name: viewLabel,
          icon: <PreviewIcon />,
          onClick: previewFile,
        },
      ]
    : [];

  const downloadFileAction =
    isDownloadAvailable || itemLabel === 'folder'
      ? [
          {
            name: `Download ${downloadSignedFile ? 'original' : ''}`,
            icon: <DownloadIcon />,
            onClick: downloadFile,
            isDisabled: isEmptyFolder,
            tooltipLabel: isEmptyFolder
              ? 'Folder with no contents cannot be downloaded.'
              : '',
          },
        ]
      : [];

  const eSignatureRequestAction = eSignatureRequest
    ? [
        {
          name: 'eSignature request',
          icon: <SendIcon />,
          onClick: eSignatureRequest,
          isDisabled: isRequestingSignatureDisabled(),
          tooltipLabel: UPGRADE_TOOLTIP_LABEL,
        },
      ]
    : [];

  const cancelESignatureRequestAction = cancelESignatureRequest
    ? [
        {
          name: 'Cancel eSignature request',
          icon: <CancelIcon />,
          onClick: cancelESignatureRequest,
          isDisabled: !eSignEnabledInPlan,
          tooltipLabel: UPGRADE_TOOLTIP_LABEL,
        },
      ]
    : [];

  const downloadSignedFileAction = downloadSignedFile
    ? [
        {
          name: `Download`,
          icon: <DownloadIcon />,
          onClick: downloadSignedFile,
          isDisabled: !eSignEnabledInPlan,
          tooltipLabel: UPGRADE_TOOLTIP_LABEL,
        },
      ]
    : [];

  const handleRemoveFileClicked = () => {
    removeFile(data);
  };

  const handleRenameFileClicked = () => {
    renameFile(data);
  };

  const deleteSettingDisabled = React.useMemo(() => {
    // base state is that delete is disabled if file delete is disabled
    let isDeleteDisabled = Boolean(fileSettings?.clientFileDeleteDisabled);

    // this can be overridden if folder update disable is turned off and fileType is folder
    // ...but only if this folder is empty. To delete a non-empty folder the user needs file and folder update ability
    if (fileType === FILE_TYPE.FOLDER) {
      isDeleteDisabled =
        Boolean(fileSettings?.clientFolderUpdateDisabled) ||
        (!isEmptyFolder && Boolean(fileSettings?.clientFileDeleteDisabled));
    }

    return isDeleteDisabled;
  }, [fileSettings, isEmptyFolder]);

  const actions = [
    ...downloadSignedFileAction,
    ...downloadFileAction,
    ...previewFileAction,
    {
      name: `Rename ${itemLabel}`,
      onClick: handleRenameFileClicked,
      icon: <EditIcon />,
      isDisabled:
        isClient &&
        fileType === FILE_TYPE.FOLDER &&
        Boolean(fileSettings?.clientFolderUpdateDisabled),
      tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
    },
    ...eSignatureRequestAction,
    ...cancelESignatureRequestAction,
    {
      name: `Delete ${itemLabel}`,
      onClick: handleRemoveFileClicked,
      icon: <TrashIcon />,
      isDelete: true,
      isDisabled: isClient && deleteSettingDisabled,
      tooltipLabel: FILE_ACTION_DISABLE_TOOLTIP_LABEL,
    },
  ];

  return eSignDocument ? (
    <Button color="primary" variant="contained" onClick={eSignDocument}>
      eSign
    </Button>
  ) : (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={compact(actions)}
    />
  );
};

export default FilesActionsRenderer;
